import React, { useCallback, useEffect, useRef, useState } from "react";
import { getChargesByChhIdAndChargeCategoryNotGenerated } from "../../../../api/finance/chargeConfirmation";
import { getJobInfo } from "../../../../api/finance/addChargesApi";
import { formatFromJsonDate } from "../../../../dateConfig";
import LoadingSpinner from "../../../LoadingSpinner";
import InfoList from "../../../InfoList";
import Grid from "../../../Grid";
import { FORMS, MODULES } from "../../../../const";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../input/Button";
import {
  CurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../../renderer/StyleRenderer";
import Input from "../../../input/Input";
import { getBulkDropdownData } from "../../../../api/keyvalue";
import Dropdown from "../../../input/Dropdown";
import { invEvActions } from "../../../../store/finance/inv-ev/inv-ev-slice";
import { updateSingleInvEv } from "../../../../store/finance/inv-ev/inv-ev-actions";
import { reportActions } from "../../../../store/report/report-slice";
import { toast } from "react-toastify";
import ChargeTaxBreakdown from "../ChargeTaxBreakdown";
import { addAuditRecord } from "../../../../api/auditApi";
import useUserCompany from "../../../../hooks/useUserCompany";

const HEADER_FORM = FORMS.EV_INV_MANAGER.HEADER.form;

const SingleEvInvComponent = ({
  getGridData,
  username,
  openTaxBreakdown,
  openReportModal,
  commonNoConsolidationView,
  formatter,
}) => {
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);

  const [jobInfo, setJobInfo] = useState(null);

  const isEditMode = useSelector((state) => state.invEv.editMode);
  const savedUpdateResponse = useSelector(
    (state) => state.invEv.fetchedResponse,
  );
  const categoryId = useSelector((state) => state.invEv.categoryId);
  const jobId = useSelector((state) => state.invEv.jobId);
  const chhId = useSelector((state) => state.invEv.chhId);
  const isSubmitLoading = useSelector((state) => state.invEv.submitLoading);
  const dispatch = useDispatch();

  const company = useUserCompany();
  const user = useSelector((state) => state.user);

  const evInvHeaderPermissions = auth[HEADER_FORM];
  const singleGenEvPermission = evInvHeaderPermissions?.FORM?.SINGLE_GEN_EV;
  const singleGenInvPermission = evInvHeaderPermissions?.FORM?.SINGLE_GEN_INV;
  const singleDownloadEvPermission =
    evInvHeaderPermissions?.FORM?.DOWNLOAD_SINGLE_EV;
  const singleDownloadInvPermission =
    evInvHeaderPermissions?.FORM?.DOWNLOAD_SINGLE_INV;

  const withoutTaxTotal = useSelector((state) => state.invEv.withoutTaxTotal);
  const withTaxTotal = useSelector((state) => state.invEv.withTaxTotal);
  const taxAmount = useSelector((state) => state.invEv.taxAmount);

  const [statusOptions, setStatusOptions] = useState([]);
  const [status, setStatus] = useState(2);
  const [remark, setRemark] = useState("");
  const [selectedCharge, setSelectedCharge] = useState(null);

  const [statusLoading, setStatusLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      const { status: statusData } = await getBulkDropdownData([
        { key: "status" },
      ]);
      setStatusOptions(statusData);
      setStatusLoading(false);
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isEditMode) {
      dispatch(
        updateSingleInvEv({
          eihId: savedUpdateResponse.eihId,
          eihRemarks: remark,
          author: username,
        }),
      );
    } else {
      await setRequest();
      openTaxBreakdown();
    }
  };

  const setRequest = async () => {
    dispatch(
      invEvActions.setRequest({
        charges: selectedRows,
        hasShipperWiseData: false,
        shipperWiseData: {},
      }),
    );
  };

  const gridRef = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [jobInfoSummary, setJobInfoSummary] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const resetLocalState = useSelector((state) => state.invEv.resetLocalState);

  const remarkRef = useRef("");

  useEffect(() => {
    if (resetLocalState) {
      setSelectedRows([]);
    }
    dispatch(invEvActions.setResetLocalState(false));
  }, [dispatch, resetLocalState]);

  useEffect(() => {
    (async () => {
      if (!jobId || !chhId) {
        return;
      }
      setIsLoading(true);
      try {
        const charges = await getChargesByChhIdAndChargeCategoryNotGenerated(
          chhId,
          categoryId,
        );
        // Fallback validation for invalid status.
        if (
          !isEditMode &&
          ((categoryId === 1 &&
            charges?.jobChargeHeader &&
            charges?.jobChargeHeader?.chhInvStatus !== 3 &&
            charges?.jobChargeHeader?.chhInvStatus !== 6) ||
            (categoryId === 2 &&
              charges?.jobChargeHeader &&
              charges?.jobChargeHeader?.chhEvStatus !== 3 &&
              charges?.jobChargeHeader?.chhEvStatus !== 6))
        ) {
          toast.warn("Charge with invalid status found! Please try again.", {
            autoClose: 5000,
            containerId: "Main",
          });
          dispatch(invEvActions.resetState());
          return;
        }
        const response = await getJobInfo(jobId);
        const jobResponse = response[0];
        dispatch(
          invEvActions.setCurrencyAndStakeholder({
            currencyId: charges.jobChargeHeader.chhCurrency,
            stakeholderId:
              categoryId === 1
                ? charges.jobChargeHeader.chhCustomer
                : charges.jobChargeHeader.chhSupplier,
          }),
        );
        dispatch(invEvActions.setJobInfo(jobResponse));
        setJobInfo(jobResponse);
        const customerName = jobResponse.customerName;
        const transporterName = jobResponse.transporterName;
        const currencyName = charges.jobChargeHeader.chhCurrencyName;

        const jobInfoObject = [
          {
            title: "EV or INV",
            value: categoryId === 1 ? "INV Generator" : "EV Generator",
          },
          {
            title: categoryId === 1 ? "INV#" : "EV#",
            value: isEditMode
              ? savedUpdateResponse
                ? savedUpdateResponse?.eihCode
                : "N/A"
              : "N/A",
          },
          { title: "Job No:", value: jobResponse.jobCode },
          { title: "Request Code:", value: jobResponse.reqCode },
          {
            title: "Customer",
            value: customerName ?? "N/A",
          },
          {
            title: "Transporter",
            value: transporterName ?? "N/A",
          },
          {
            title: "Shipper",
            value: jobResponse.shipperName ? jobResponse.shipperName : "N/A",
          },
          {
            title: "Invoicing Customer",
            value: jobResponse.invoicingCustomer
              ? jobResponse.invoicingCustomer
              : "N/A",
          },
          {
            title: "From Date",
            value: jobResponse.fromDate
              ? formatFromJsonDate(jobResponse.fromDate)
              : "N/A",
          },
          {
            title: "To Date",
            value: jobResponse.toDate
              ? formatFromJsonDate(jobResponse.toDate)
              : "N/A",
          },
          {
            title: "Booking Required Date",
            value: jobResponse.bookingRequiredDate
              ? formatFromJsonDate(jobResponse.bookingRequiredDate)
              : "N/A",
          },
          {
            title: "Currency",
            value: currencyName,
          },
          {
            title: "Chargeable Value",
            value: jobResponse.chargeableValue
              ? jobResponse.chargeableValue
              : "N/A",
          },
          {
            title: "Vehicle/Cont",
            value: jobResponse.vhtType ? jobResponse.vhtType : "N/A",
          },
          {
            title: "Vehicle No.",
            value: jobResponse.vehVehicleno ? jobResponse.vehVehicleno : "N/A",
          },
          {
            title: "Containers",
            value: jobResponse.conNumbers ? jobResponse.conNumbers : "N/A",
          },
        ];
        setJobInfoSummary(jobInfoObject);

        if (!isEditMode) {
          const gridDataWithChargeType = [];
          for (const chargeDetails of charges.jobChargeDetails) {
            gridDataWithChargeType.push({
              ...chargeDetails,
              jobCode: jobResponse.jobCode,
              chgCharges: chargeDetails.chdChacrgesValue,
              chargeStatusName: chargeDetails.chdStatusValue,
            });
          }
          setGridData(gridDataWithChargeType);
        } else {
          dispatch(
            invEvActions.setTaxInfo({
              withoutTaxTotal: savedUpdateResponse.eihWithoutTax,
              withTaxTotal: savedUpdateResponse.eihWithTax,
              taxAmount:
                savedUpdateResponse.eihWithTax -
                savedUpdateResponse.eihWithoutTax,
            }),
          );
          setRemark(savedUpdateResponse.eihRemarks);
          remarkRef.current = savedUpdateResponse.eihRemarks;
          setStatus(savedUpdateResponse.eihStatus);

          const gridDataWithChargeType = await getGridData(savedUpdateResponse);

          setGridData(
            gridDataWithChargeType.map((data) => ({
              ...data,
              jobCode: jobResponse.jobCode,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isEditMode, savedUpdateResponse, categoryId, jobId]);

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const onDownload = async () => {
    await addAuditRecord({
      company: company ? company.comId : null,
      action: "Single EV/Inv Excel Download",
      params: "",
      type: 6,
      module: 5,
      user: user.username,
    });
    dispatch(
      reportActions.setEvInvoiceReportData({
        jobInfo: jobInfo,
        fetchedResponse: savedUpdateResponse,
        moduleCategory: categoryId === 1 ? 2 : 3,
      }),
    );
    openReportModal();
  };

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <InfoList
            content={jobInfoSummary}
            title={isEditMode ? "EV/Invoice Summary" : "Charges Summary"}
          />
          {isEditMode && (
            <ChargeTaxBreakdown
              formatter={formatter}
              selectedCharge={selectedCharge}
            />
          )}
          <div className="ui segment">
            <Grid
              cellClickHandler={(e) => {
                setSelectedCharge(e.data);
              }}
              module={MODULES.Finance}
              ref={gridRef}
              multiRowSelection={true}
              onSelectionChanged={handleSelectionChanged}
              showActionMenu={false}
              heading={`${isEditMode ? "" : "Generate"} ${categoryId === 1 ? "Invoice" : "Expense Voucher"} (${categoryId === 1 ? "INV" : "EV"})`}
              content={gridData}
              config={
                isEditMode
                  ? [
                      {
                        headerName: "Charges",
                        field: "chgCharges",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRenderer: ResizableScrollingTextRenderer,
                        cellRendererParams: (params) => ({
                          threshold: 15,
                        }),
                      },
                      ...commonNoConsolidationView,
                      {
                        headerName: "TAX",
                        field: "chdTaxAmount",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRendererSelector: (param) => {
                          if (param.data.chdTaxAmount === -1) {
                            return {
                              component: () => "Not yet calculated",
                            };
                          }
                          return {
                            component: CurrencyRenderer,
                            params: {
                              formatter: formatter,
                            },
                          };
                        },
                      },
                      {
                        headerName: "With Tax Total",
                        field: "chdTotalWithTax",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRendererSelector: (param) => {
                          if (param.data.chdTotalWithTax === -1) {
                            return {
                              component: () => "Not yet calculated",
                            };
                          }
                          return {
                            component: CurrencyRenderer,
                            params: {
                              formatter: formatter,
                            },
                          };
                        },
                      },
                    ]
                  : [
                      {
                        headerName: "Charges",
                        field: "chgCharges",
                        authenticated: true,
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRenderer: ResizableScrollingTextRenderer,
                        cellRendererParams: (params) => ({
                          threshold: 15,
                        }),
                      },
                      ...commonNoConsolidationView,
                    ]
              }
            />
          </div>
          {isEditMode && (
            <div className="ui form" style={{ marginTop: "10px" }}>
              <div className="three fields">
                <Input
                  field={"eih_without_tax"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  type="text"
                  label="Without Tax"
                  placeholder="Without Tax"
                  readOnly={true}
                  value={
                    formatter ? formatter.format(withoutTaxTotal) : withTaxTotal
                  }
                />
                <Input
                  field={"eih_with_tax"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  type="text"
                  label="Tax"
                  placeholder="Tax"
                  readOnly={true}
                  value={formatter ? formatter.format(taxAmount) : taxAmount}
                />
                <Input
                  field={"eih_total"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  type="text"
                  label="Total"
                  placeholder="Total"
                  readOnly={true}
                  value={
                    formatter ? formatter.format(withTaxTotal) : withTaxTotal
                  }
                />
              </div>
              <div className="two fields">
                <Input
                  required={isEditMode}
                  field={"eih_remarks"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  authenticated={true}
                  type="text"
                  label="Remarks"
                  placeholder="Remarks"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />

                <Dropdown
                  field={"eih_status"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  authenticated={true}
                  required={true}
                  clearable={false}
                  readOnly={true}
                  label="Status"
                  placeholder={statusLoading ? "Loading ..." : "Status"}
                  options={statusOptions}
                  style={{ height: "20px" }}
                  value={status}
                  onChange={(e, data) => {
                    setStatus(data.value);
                  }}
                />
              </div>
            </div>
          )}
          <div
            className="two fields"
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <Button
              template="create"
              isLoading={isSubmitLoading}
              create={!isEditMode}
              onClick={onSubmit}
              form={HEADER_FORM}
              module={MODULES.Finance}
              authenticated={true}
              editMode={isEditMode}
              disabled={
                (categoryId === 1 && !singleGenInvPermission) ||
                (categoryId === 2 && !singleGenEvPermission) ||
                (selectedRows.length <= 0 &&
                  (!isEditMode || remark === remarkRef.current))
              }
            />

            <Button
              label="Download EV/INV"
              onClick={onDownload}
              disabled={
                !isEditMode ||
                (categoryId === 1 && !singleDownloadInvPermission) ||
                (categoryId === 2 && !singleDownloadEvPermission)
              }
            />
          </div>
        </>
      )}
    </>
  );
};

export default SingleEvInvComponent;
